import { useMemo } from 'react';

import { can, type PermissionKey } from './permissions';
import { useAuth } from './useAuth';

type Capitalize_<S extends string> = S extends `${infer F}${infer R}`
  ? `${Uppercase<F>}${R}`
  : S;

export type Permissions = {
  readonly [K in PermissionKey as `can${Capitalize_<K>}`]: boolean;
};

/**
 * Component-friendly wrapper over `can()`. Components destructure the named
 * gates they need:
 *
 * ```tsx
 * const { canBrowsePatients, canCreatePatients } = usePermissions();
 * ```
 *
 * Adding a new gate: append to `RULES` in `permissions.ts`, then add a line
 * here. TypeScript will fail the build until the hook covers every key.
 */
export function usePermissions(): Permissions {
  const { user } = useAuth();
  return useMemo<Permissions>(
    () => ({
      canBrowsePatients: can(user, 'browsePatients'),
      canCreatePatients: can(user, 'createPatients'),
      canEditCareTeam: can(user, 'editCareTeam'),
      canCreateFamilies: can(user, 'createFamilies'),
      canOpenAdmin: can(user, 'openAdmin'),
      canOpenPedigree: can(user, 'openPedigree'),
      canManageConfig: can(user, 'manageConfig'),
      canOpenPlatform: can(user, 'openPlatform'),
      canManagePlatform: can(user, 'managePlatform'),
    }),
    [user],
  );
}
